/**
 * "Meu perfil" — por enquanto, a foto (061, 25/09/2026).
 *
 * Abre pelo bloco do usuário no rodapé do menu, que era só leitura. É o lugar
 * natural para crescer depois — nome, senha, segundo fator —, e por isso é
 * uma janela de perfil, e não um botão de foto solto.
 *
 * ── A imagem sai daqui já pequena ─────────────────────────────────────────
 *
 * Foto de celular tem 4 MB e 4000 pixels de lado, e o menu mostra 34. A tela
 * recorta o centro num quadrado e reduz para 256×256 em JPEG antes de mandar:
 * o envio vira dezenas de kilobytes, e a API guarda o que vai mostrar. Quem
 * confere o que chegou continua sendo o servidor, pelos primeiros bytes.
 */

import { useRef, useState, type ReactNode } from 'react';
import { LIMITES_DA_FOTO } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { useSessao } from '@/shared/sessao/contexto';
import { Botao, Dialogo } from '@/shared/ui';
import { FotoOuIniciais } from '@/shared/ui/foto-ou-iniciais/foto-ou-iniciais';
import './meu-perfil.css';

/** Recorta o centro num quadrado e reduz ao lado da foto, em JPEG. */
async function reduzir(arquivo: File): Promise<Blob> {
  const imagem = await createImageBitmap(arquivo);
  const lado = Math.min(imagem.width, imagem.height);
  const destino = LIMITES_DA_FOTO.lado;
  const tela = document.createElement('canvas');
  tela.width = destino;
  tela.height = destino;
  const contexto = tela.getContext('2d');
  if (!contexto) throw new Error('O navegador não conseguiu preparar a imagem.');
  contexto.drawImage(
    imagem,
    (imagem.width - lado) / 2,
    (imagem.height - lado) / 2,
    lado,
    lado,
    0,
    0,
    destino,
    destino,
  );
  imagem.close();
  return new Promise((resolver, rejeitar) =>
    tela.toBlob(
      (blob) =>
        blob ? resolver(blob) : rejeitar(new Error('Não foi possível preparar a imagem.')),
      'image/jpeg',
      0.85,
    ),
  );
}

export function MeuPerfil({ aoFechar }: { aoFechar: () => void }): ReactNode {
  const sessao = useSessao();
  const api = useApi();
  const campo = useRef<HTMLInputElement>(null);
  const [ocupado, definirOcupado] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  if (sessao.estado.nome !== 'autenticado') return null;
  const { usuario } = sessao.estado.me;

  const enviar = async (arquivo: File): Promise<void> => {
    if (!api) return;
    definirOcupado(true);
    definirErro(null);
    try {
      const reduzida = await reduzir(arquivo);
      const formulario = new FormData();
      formulario.append('foto', reduzida, 'foto.jpg');
      const { foto_url } = await api.definirFotoDePerfil({ corpo: formulario });
      sessao.definirFoto(foto_url);
    } catch (causa: unknown) {
      definirErro(
        recadoDeErro(causa, { generica: 'Não foi possível trocar a foto. Tente de novo.' }),
      );
    } finally {
      definirOcupado(false);
      // Escolher o MESMO arquivo de novo precisa disparar a troca outra vez.
      if (campo.current) campo.current.value = '';
    }
  };

  const remover = async (): Promise<void> => {
    if (!api) return;
    definirOcupado(true);
    definirErro(null);
    try {
      await api.removerFotoDePerfil();
      sessao.definirFoto(null);
    } catch (causa: unknown) {
      definirErro(
        recadoDeErro(causa, { generica: 'Não foi possível tirar a foto. Tente de novo.' }),
      );
    } finally {
      definirOcupado(false);
    }
  };

  return (
    <Dialogo
      titulo="Meu perfil"
      aoFechar={aoFechar}
      botoes={
        <Botao variante="primario" onClick={aoFechar}>
          Pronto
        </Botao>
      }
    >
      <div className="meu-perfil">
        <FotoOuIniciais nome={usuario.nome} foto={usuario.foto_url} tamanho={96} />
        <div className="meu-perfil__quem">
          <b>{usuario.nome}</b>
          <span>{usuario.email}</span>
        </div>
      </div>

      <div className="meu-perfil__acoes">
        {/*
          O campo de arquivo fica escondido e o botão o aciona: o seletor
          nativo não se estiliza, e o texto dele muda com o idioma do sistema.
        */}
        <input
          ref={campo}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="meu-perfil__campo"
          aria-label="Escolher foto"
          tabIndex={-1}
          onChange={(evento) => {
            const escolhido = evento.currentTarget.files?.[0];
            if (escolhido) void enviar(escolhido);
          }}
        />
        <Botao
          onClick={() => campo.current?.click()}
          desabilitado={ocupado || !api}
          motivo="Aguarde a foto anterior."
        >
          {ocupado ? 'Enviando…' : usuario.foto_url ? 'Trocar foto' : 'Colocar foto'}
        </Botao>
        {usuario.foto_url && (
          <Botao onClick={() => void remover()} desabilitado={ocupado} motivo="Aguarde.">
            Tirar foto
          </Botao>
        )}
      </div>

      <p className="meu-perfil__ajuda">
        JPEG, PNG ou WebP. A foto é recortada num quadrado e aparece para quem trabalha com você.
      </p>

      {erro !== null && (
        <p className="dialogo__erro" role="alert">
          {erro}
        </p>
      )}
    </Dialogo>
  );
}
